import { useEffect, useRef } from "react";
import { Play, ExternalLink } from "lucide-react";

// YouTube helpers
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  // Handle /shorts/ URLs
  const shortsMatch = url.match(/youtube\.com\/shorts\/([^&\n?#]+)/);
  if (shortsMatch) return shortsMatch[1];
  // Handle standard URLs
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

// Instagram helpers
const getInstagramPostId = (url) => {
  if (!url) return null;
  // Match patterns like: instagram.com/p/ABC123/ or instagram.com/reel/ABC123/
  const regExp = /instagram\.com\/(p|reel|tv)\/([A-Za-z0-9_-]+)/;
  const match = url.match(regExp);
  return match ? match[2] : null;
};

// TikTok helpers
const getTikTokVideoId = (url) => {
  if (!url) return null;
  // Match patterns like: tiktok.com/@user/video/1234567890 or vm.tiktok.com/ABC123/
  const regExp = /tiktok\.com\/@[^/]+\/video\/(\d+)|vm\.tiktok\.com\/([A-Za-z0-9]+)/;
  const match = url.match(regExp);
  return match ? (match[1] || match[2]) : null;
};

// YouTube Embed Component
export const YouTubeEmbed = ({ url, title }) => {
  const videoId = getYouTubeVideoId(url);
  
  if (!videoId) {
    return (
      <div className="aspect-video bg-muted flex items-center justify-center text-muted-foreground">
        Invalid YouTube URL
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="youtube-embed">
      <h2 className="font-serif text-2xl font-medium">Video Tour</h2>
      <div className="aspect-video">
        <iframe
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${videoId}?rel=0`}
          title={`${title} - Video Tour`}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
};

// Instagram Embed Component - Shows placeholder with link
export const InstagramEmbed = ({ url, title }) => {
  const postId = getInstagramPostId(url);
  const isReel = url?.includes('/reel/');
  
  if (!postId) {
    return (
      <div className="aspect-video bg-muted flex items-center justify-center text-muted-foreground">
        Invalid Instagram URL
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="instagram-embed">
      <h2 className="font-serif text-2xl font-medium">Video Tour</h2>
      <div className="relative bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-lg overflow-hidden">
        <div className="aspect-video flex flex-col items-center justify-center text-white p-8">
          {/* Instagram Logo */}
          <div className="w-20 h-20 mb-6 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-12 h-12 fill-white">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </div>
          
          <h3 className="text-xl font-semibold mb-2">
            {isReel ? 'Instagram Reel' : 'Instagram Video'}
          </h3>
          <p className="text-white/80 text-sm mb-6 text-center max-w-md">
            Watch our property video tour on Instagram
          </p>
          
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-full font-medium hover:bg-white/90 transition-colors"
          >
            <Play size={18} fill="currentColor" />
            Watch on Instagram
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </div>
  );
};

// TikTok Embed Component - Shows placeholder with link
export const TikTokEmbed = ({ url, title }) => {
  const videoId = getTikTokVideoId(url);
  
  if (!videoId) {
    return (
      <div className="aspect-video bg-muted flex items-center justify-center text-muted-foreground">
        Invalid TikTok URL
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tiktok-embed">
      <h2 className="font-serif text-2xl font-medium">Video Tour</h2>
      <div className="relative bg-black rounded-lg overflow-hidden">
        <div className="aspect-video flex flex-col items-center justify-center text-white p-8">
          {/* TikTok Logo */}
          <div className="w-20 h-20 mb-6 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-12 h-12 fill-white">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-4 right-4 w-16 h-16 bg-[#25F4EE] rounded-full opacity-20 blur-xl" />
          <div className="absolute bottom-4 left-4 w-20 h-20 bg-[#FE2C55] rounded-full opacity-20 blur-xl" />
          
          <h3 className="text-xl font-semibold mb-2">TikTok Video</h3>
          <p className="text-white/80 text-sm mb-6 text-center max-w-md">
            Watch our property video tour on TikTok
          </p>
          
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#FE2C55] text-white px-6 py-3 rounded-full font-medium hover:bg-[#FE2C55]/90 transition-colors"
          >
            <Play size={18} fill="currentColor" />
            Watch on TikTok
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </div>
  );
};

// Universal Video Embed - chooses the right component based on video type
export const VideoEmbed = ({ url, videoType = "youtube", title }) => {
  if (!url) return null;

  switch (videoType) {
    case "instagram":
      return <InstagramEmbed url={url} title={title} />;
    case "tiktok":
      return <TikTokEmbed url={url} title={title} />;
    case "youtube":
    default:
      return <YouTubeEmbed url={url} title={title} />;
  }
};

// Export helpers for use in PropertyCard
export { getYouTubeVideoId, getInstagramPostId, getTikTokVideoId };

export default VideoEmbed;
